import React, { useState } from "react";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import RadioGroup from "@cloudscape-design/components/radio-group";
import Multiselect from "@cloudscape-design/components/multiselect";
import Checkbox from "@cloudscape-design/components/checkbox";
import Alert from "@cloudscape-design/components/alert";
import Select from "@cloudscape-design/components/select";
import { useTranslation } from "react-i18next";

export default function CreateFeedModal({ visible, onDismiss, onSubmit, editFeed = null }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filterLogic, setFilterLogic] = useState("OR");
  const [regions, setRegions] = useState([]);
  const [includeGlobal, setIncludeGlobal] = useState(false); // Default: exclude global announcements (opt-in)
  const [filterGlobalByAvailability, setFilterGlobalByAvailability] = useState(true); // Default: filter by service availability (opt-out)
  const [services, setServices] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [notificationLanguage, setNotificationLanguage] = useState({ label: "English", value: "en" });
  const [slackChannelId, setSlackChannelId] = useState("");
  const [slackWebhook, setSlackWebhook] = useState("");
  const [existingWebhook, setExistingWebhook] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [slackMethod, setSlackMethod] = useState("channel_id"); // "channel_id" or "webhook"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const BUILTIN_SOURCES = [
    { label: t("form.option.data-source-news"), value: "news" },
    { label: t("form.option.data-source-blogs"), value: "blogs" },
  ];
  const [selectedSources, setSelectedSources] = useState(BUILTIN_SOURCES); // Default: both built-ins selected

  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableSourceFeeds, setAvailableSourceFeeds] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load regions and services on mount
  React.useEffect(() => {
    if (visible) {
      loadOptions();

      // If editing, populate form with existing data
      if (editFeed) {
        setName(editFeed.name || "");
        setDescription(editFeed.description || "");
        setFilterLogic(editFeed.filters?.logic || "OR");
        setKeywords((editFeed.filters?.keywords || []).join(", "));

        // Set notification language
        const langCode = editFeed.notification_language || "en";
        const langOptions = {
          en: { label: "English", value: "en" },
          ja: { label: "日本語 (Japanese)", value: "ja" },
          es: { label: "Español (Spanish)", value: "es" },
          fr: { label: "Français (French)", value: "fr" },
          pt: { label: "Português (Portuguese)", value: "pt" },
        };
        setNotificationLanguage(langOptions[langCode] || langOptions.en);

        setSlackChannelId(editFeed.delivery?.slack_channel_id || "");
        // slack_webhook is sanitized to a boolean by the backend (to hide the URL).
        // Treat any truthy value as "already configured" but don't populate the input.
        const webhookVal = editFeed.delivery?.slack_webhook;
        setSlackWebhook(typeof webhookVal === "string" ? webhookVal : "");
        setExistingWebhook(Boolean(webhookVal));
        setChannelName(editFeed.delivery?.channel_name || "");
        // Determine which method was used
        if (editFeed.delivery?.slack_webhook) {
          setSlackMethod("webhook");
        } else {
          setSlackMethod("channel_id");
        }

        // Set regions and services after options are loaded
        setTimeout(() => {
          if (editFeed.filters?.regions) {
            setRegions(editFeed.filters.regions.map((r) => ({ label: r, value: r })));
          }
          if (editFeed.filters?.services) {
            setServices(editFeed.filters.services.map((s) => ({ label: s, value: s })));
          }
          // Populate unified sources: built-ins from data_sources + external from source_feed_ids
          const builtinSelected = (editFeed.filters?.data_sources || ["news", "blogs"]).map((v) =>
            v === "news" ? { label: t("form.option.data-source-news"), value: "news" } : { label: t("form.option.data-source-blogs"), value: "blogs" },
          );
          const externalSelected = (editFeed.filters?.source_feed_ids || []).map((id) => ({ label: id, value: id }));
          setSelectedSources([...builtinSelected, ...externalSelected]);
        }, 100);

        // Set include_global (defaults to false if not specified)
        const includeGlobalValue = editFeed.filters?.include_global;
        setIncludeGlobal(includeGlobalValue === true || includeGlobalValue === "true");

        const filterGlobalValue = editFeed.filters?.filter_global_by_availability;
        setFilterGlobalByAvailability(filterGlobalValue === true || filterGlobalValue === "true");
      } else {
        // Reset form for new feed
        setName("");
        setDescription("");
        setFilterLogic("OR");
        setRegions([]);
        setIncludeGlobal(false);
        setFilterGlobalByAvailability(true);
        setServices([]);
        setKeywords("");
        setSelectedSources(BUILTIN_SOURCES);
        setNotificationLanguage({ label: "English", value: "en" });
        setSlackChannelId("");
        setSlackWebhook("");
        setChannelName("");
        setSlackMethod("channel_id");
      }
    }
  }, [visible, editFeed]);

  const loadOptions = async () => {
    setLoadingOptions(true);
    try {
      const { api } = await import("../api");
      const [regionsData, servicesData, sourceFeedsData] = await Promise.all([api.getRegions(), api.getServices(), api.getSourceFeeds()]);

      setAvailableRegions(regionsData.regions || []);
      setAvailableServices((servicesData.services || []).map((s) => ({ label: s, value: s })));
      setAvailableSourceFeeds(
        (sourceFeedsData.source_feeds || []).map((f) => ({
          label: f.name,
          value: f.name,
          description: f.description || "",
        })),
      );
    } catch (err) {
      console.error("Error loading options:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleSubmit = async () => {
    setError("");

    if (!name.trim()) {
      setError(t("form.error.name-required"));
      return;
    }

    if (selectedSources.length === 0) {
      setError(t("form.error.sources-required"));
      return;
    }

    // Split unified sources back into data_sources (built-ins) and source_feed_ids (external)
    const builtinValues = ["news", "blogs"];
    const data_sources = selectedSources.filter((s) => builtinValues.includes(s.value)).map((s) => s.value);
    const source_feed_ids = selectedSources.filter((s) => !builtinValues.includes(s.value)).map((s) => s.value);

    setLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        notification_language: notificationLanguage.value,
        filters: {
          logic: filterLogic,
          regions: regions.map((r) => r.value),
          include_global: Boolean(includeGlobal),
          filter_global_by_availability: Boolean(filterGlobalByAvailability),
          services: services.map((s) => s.value),
          keywords: keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k),
          data_sources,
          source_feed_ids,
        },
        delivery: channelName.trim()
          ? {
              channel_name: channelName.trim(),
              ...(slackMethod === "channel_id" && slackChannelId.trim() ? { slack_channel_id: slackChannelId.trim() } : {}),
              ...(slackMethod === "webhook" && slackWebhook.trim() ? { slack_webhook: slackWebhook.trim() } : {}),
            }
          : {},
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={editFeed ? t("form.modal.edit-title") : t("form.modal.create-title")}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              {t("feeds.action.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={loading}>
              {editFeed ? t("form.action.save-changes") : t("form.action.create")}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}

        <FormField label={t("form.field.name")} description={t("form.field.name-description")}>
          <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder={t("form.field.name-placeholder")} />
        </FormField>

        <FormField label={t("form.field.description")} description={t("form.field.description-description")}>
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            placeholder={t("form.field.description-placeholder")}
            rows={3}
          />
        </FormField>

        <FormField label={t("form.field.filter-logic")} description={t("form.field.filter-logic-description")}>
          <RadioGroup
            value={filterLogic}
            onChange={({ detail }) => setFilterLogic(detail.value)}
            items={[
              { value: "OR", label: t("form.option.match-any"), description: t("form.option.match-any-description") },
              { value: "AND", label: t("form.option.match-all"), description: t("form.option.match-all-description") },
            ]}
          />
        </FormField>

        <FormField label={t("form.field.sources")} description={t("form.field.sources-description")}>
          <Multiselect
            selectedOptions={selectedSources}
            onChange={({ detail }) => setSelectedSources(detail.selectedOptions)}
            options={[
              { label: t("form.option.data-source-news"), value: "news" },
              { label: t("form.option.data-source-blogs"), value: "blogs" },
              ...availableSourceFeeds,
            ]}
            placeholder={loadingOptions ? t("form.field.loading-sources") : t("form.field.sources-placeholder")}
            filteringType="auto"
            disabled={loadingOptions}
          />
        </FormField>

        <FormField label={t("form.field.notification-language")} description={t("form.field.notification-language-description")}>
          <Select
            selectedOption={notificationLanguage}
            onChange={({ detail }) => setNotificationLanguage(detail.selectedOption)}
            options={[
              { label: "English", value: "en" },
              { label: "日本語 (Japanese)", value: "ja" },
              { label: "Español (Spanish)", value: "es" },
              { label: "Français (French)", value: "fr" },
              { label: "Português (Portuguese)", value: "pt" },
            ]}
            placeholder={t("form.field.notification-language-placeholder")}
          />
        </FormField>

        <FormField label={t("form.field.regions")} description={t("form.field.regions-description")}>
          <Multiselect
            selectedOptions={regions}
            onChange={({ detail }) => setRegions(detail.selectedOptions)}
            options={availableRegions}
            placeholder={
              loadingOptions
                ? t("form.field.loading-regions")
                : regions.length === 0
                  ? t("form.field.regions-placeholder")
                  : t("form.field.regions-placeholder-active")
            }
            filteringType="auto"
            disabled={loadingOptions}
          />
        </FormField>

        <FormField>
          <SpaceBetween size="s">
            <Checkbox checked={includeGlobal} onChange={({ detail }) => setIncludeGlobal(detail.checked)}>
              {t("form.field.include-global-announcements")}
            </Checkbox>
            <Box margin={{ left: "xxl" }}>
              <Checkbox checked={filterGlobalByAvailability} onChange={({ detail }) => setFilterGlobalByAvailability(detail.checked)} disabled={!includeGlobal}>
                {t("form.field.filter-global-by-availability")}
              </Checkbox>
            </Box>
          </SpaceBetween>
        </FormField>

        <FormField label={t("form.field.services")} description={t("form.field.services-description")}>
          <Multiselect
            selectedOptions={services}
            onChange={({ detail }) => setServices(detail.selectedOptions)}
            options={availableServices}
            placeholder={
              loadingOptions
                ? t("form.field.loading-services")
                : services.length === 0
                  ? t("form.field.services-placeholder")
                  : t("form.field.services-placeholder-active")
            }
            filteringType="auto"
            disabled={loadingOptions}
          />
        </FormField>

        <FormField label={t("form.field.keywords")} description={t("form.field.keywords-description")}>
          <Input value={keywords} onChange={({ detail }) => setKeywords(detail.value)} placeholder={t("form.field.keywords-placeholder")} />
        </FormField>

        <Alert type="info">{t("form.info.rss-info")}</Alert>

        <FormField label={t("form.field.slack-channel-name")} description={t("form.field.slack-channel-name-description")}>
          <Input value={channelName} onChange={({ detail }) => setChannelName(detail.value)} placeholder={t("form.field.slack-channel-name-placeholder")} />
        </FormField>

        {channelName.trim() && (
          <>
            <FormField label={t("form.field.slack-method")} description={t("form.field.slack-method-description")}>
              <RadioGroup
                value={slackMethod}
                onChange={({ detail }) => setSlackMethod(detail.value)}
                items={[
                  {
                    value: "channel_id",
                    label: t("form.option.channel-id-recommended"),
                    description: t("form.option.channel-id-description"),
                  },
                  {
                    value: "webhook",
                    label: t("form.option.webhook-url"),
                    description: t("form.option.webhook-description"),
                  },
                ]}
              />
            </FormField>

            {slackMethod === "channel_id" && (
              <FormField
                label={t("form.field.slack-channel-id")}
                description={
                  <Box>
                    <SpaceBetween size="xxs">
                      <div>{t("form.help.slack-channel-id-step1")}</div>
                      <div>
                        {t("form.help.slack-channel-id-step2")} <strong>{t("form.help.slack-channel-id-important")}</strong>{" "}
                        {t("form.help.slack-channel-id-invite")} <code>/invite @relevant_news_updates</code>
                      </div>
                    </SpaceBetween>
                  </Box>
                }
              >
                <Input
                  value={slackChannelId}
                  onChange={({ detail }) => setSlackChannelId(detail.value)}
                  placeholder={t("form.field.slack-channel-id-placeholder")}
                />
              </FormField>
            )}

            {slackMethod === "webhook" && (
              <FormField
                label={t("form.field.slack-webhook-url")}
                description={
                  existingWebhook && !slackWebhook
                    ? t("form.field.slack-webhook-existing")
                    : t("form.field.slack-webhook-description")
                }
              >
                <Input
                  value={slackWebhook}
                  onChange={({ detail }) => setSlackWebhook(detail.value)}
                  placeholder={existingWebhook && !slackWebhook ? t("form.field.slack-webhook-placeholder-existing") : t("form.field.slack-webhook-placeholder")}
                  type="url"
                />
              </FormField>
            )}
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
}
